import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Input,
  Progress,
  Avatar,
  Tooltip,
  Modal,
  Select,
  Form,
  Divider,
} from "antd";
import {
  FaTasks,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaProjectDiagram,
  FaUser,
  FaClock,
  FaStar,
  FaClipboardCheck,
  FaBan,
  FaLightbulb,
  FaUsers,
  FaTools,
  FaCommentAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import "./AprovacaoTarefas.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

/* ─────────────────────────────────────────────────────────────────────────────
   STAR RATING COMPONENT
───────────────────────────────────────────────────────────────────────────── */
const StarRating = ({ value, onChange, classificacoes }) => {
  const [hovered, setHovered] = useState(null);

  const activeValue = hovered ?? value;
  const label = classificacoes.find((c) => c.nota === activeValue)?.avaliacao || "";

  const colorMap = {
    1: "#ff4d4f",
    2: "#ff7a45",
    3: "#faad14",
    4: "#52c41a",
    5: "#1677ff",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(null)}
            style={{
              cursor: "pointer",
              fontSize: 24,
              color: activeValue >= star ? (colorMap[activeValue] || "#faad14") : "#d9d9d9",
              transition: "color 0.15s, transform 0.1s",
              transform: activeValue >= star ? "scale(1.15)" : "scale(1)",
              display: "inline-block",
            }}
          >
            ★
          </span>
        ))}
        {label && (
          <Text
            style={{
              marginLeft: 8,
              fontSize: 12,
              fontWeight: 600,
              color: colorMap[activeValue] || "#8c8c8c",
              minWidth: 90,
            }}
          >
            {label}
          </Text>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE REJEIÇÃO
───────────────────────────────────────────────────────────────────────────── */
const RejeitarModal = ({ visible, onConfirm, onCancel, loading }) => {
  const [motivo, setMotivo] = useState("");

  const handleClose = () => {
    setMotivo("");
    onCancel();
  };

  const handleConfirm = () => {
    if (!motivo.trim()) {
      toast.warn("Por favor, informe o motivo da rejeição.");
      return;
    }
    onConfirm(motivo);
    setMotivo("");
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={520}
      centered
      destroyOnClose
      styles={{
        content: { borderRadius: 16, padding: 0, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)",
          padding: "24px 28px",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.2)",
            borderRadius: 10,
            padding: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaBan style={{ fontSize: 22, color: "#fff" }} />
        </div>
        <div>
          <Title level={4} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
            Rejeitar Tarefa
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            Informe o motivo da rejeição
          </Text>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px 28px" }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 14 }}>
            Motivo da rejeição <span style={{ color: "#ff4d4f" }}>*</span>
          </Text>
        </div>
        <TextArea
          value={motivo}
          onChange={(e) => {
            if (e.target.value.length <= 200) setMotivo(e.target.value);
          }}
          placeholder="Descreva o motivo pelo qual esta tarefa está sendo rejeitada..."
          autoSize={{ minRows: 4, maxRows: 6 }}
          style={{ borderRadius: 10, fontSize: 14, resize: "none" }}
          showCount
          maxLength={200}
        />
        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
          Este motivo será visível ao responsável da tarefa.
        </Text>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "16px 28px",
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        <Button onClick={handleClose} style={{ borderRadius: 8 }}>
          Cancelar
        </Button>
        <Button
          danger
          type="primary"
          loading={loading}
          onClick={handleConfirm}
          icon={<FaTimesCircle style={{ marginRight: 6 }} />}
          style={{ borderRadius: 8, fontWeight: 600 }}
          disabled={!motivo.trim()}
        >
          Confirmar Rejeição
        </Button>
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE APROVAÇÃO / AVALIAÇÃO
───────────────────────────────────────────────────────────────────────────── */
const AvaliacaoModal = ({ visible, onConfirm, onCancel, loading, classificacoes }) => {
  const initialRatings = {
    qualidade_entrega: 0,
    espirito_equipa: 0,
    iniciativa_proatividade: 0,
    resolucao_problema: 0,
  };

  const [ratings, setRatings] = useState(initialRatings);
  const [recomendacao, setRecomendacao] = useState("");

  const criterios = [
    {
      key: "qualidade_entrega",
      label: "Qualidade de Entrega",
      icon: <FaClipboardCheck />,
      color: "#1677ff",
      desc: "Avalie a qualidade e completude do trabalho entregue",
    },
    {
      key: "espirito_equipa",
      label: "Espírito de Equipa",
      icon: <FaUsers />,
      color: "#722ed1",
      desc: "Colaboração, comunicação e suporte aos colegas",
    },
    {
      key: "iniciativa_proatividade",
      label: "Iniciativa e Proatividade",
      icon: <FaLightbulb />,
      color: "#fa8c16",
      desc: "Capacidade de agir sem necessitar de direção constante",
    },
    {
      key: "resolucao_problema",
      label: "Resolução de Problemas",
      icon: <FaTools />,
      color: "#52c41a",
      desc: "Eficácia ao identificar e resolver obstáculos",
    },
  ];

  const allRated = Object.values(ratings).every((v) => v > 0);

  const handleClose = () => {
    setRatings(initialRatings);
    setRecomendacao("");
    onCancel();
  };

  const handleConfirm = () => {
    if (!allRated) {
      toast.warn("Por favor, avalie todos os critérios antes de aprovar.");
      return;
    }
    onConfirm({ ...ratings, recomendacao });
    setRatings(initialRatings);
    setRecomendacao("");
  };

  const mediaGeral =
    allRated
      ? (Object.values(ratings).reduce((a, b) => a + b, 0) / 4).toFixed(1)
      : null;

  const mediaColor = mediaGeral
    ? mediaGeral >= 4.5
      ? "#1677ff"
      : mediaGeral >= 3.5
      ? "#52c41a"
      : mediaGeral >= 2.5
      ? "#faad14"
      : "#ff4d4f"
    : "#d9d9d9";

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      styles={{
        content: { borderRadius: 16, padding: 0, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
          padding: "24px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              background: "rgba(255,255,255,0.2)",
              borderRadius: 10,
              padding: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaStar style={{ fontSize: 22, color: "#fff" }} />
          </div>
          <div>
            <Title level={4} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
              Avaliar & Aprovar Tarefa
            </Title>
            <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
              Avalie o desempenho antes de aprovar
            </Text>
          </div>
        </div>

        {mediaGeral && (
          <div
            style={{
              background: "rgba(255,255,255,0.15)",
              borderRadius: 12,
              padding: "8px 16px",
              textAlign: "center",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <div
              style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1 }}
            >
              {mediaGeral}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>
              Média Geral
            </div>
          </div>
        )}
      </div>

      {/* Critérios */}
      <div style={{ padding: "20px 28px", maxHeight: "60vh", overflowY: "auto" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {criterios.map((c) => (
            <div
              key={c.key}
              style={{
                background: "#fafafa",
                borderRadius: 12,
                padding: "16px 20px",
                border: "1px solid #f0f0f0",
                transition: "border-color 0.2s",
                borderColor: ratings[c.key] > 0 ? c.color + "40" : "#f0f0f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    background: c.color + "15",
                    color: c.color,
                    borderRadius: 8,
                    padding: "6px 8px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 14,
                  }}
                >
                  {c.icon}
                </div>
                <div>
                  <Text strong style={{ fontSize: 14, display: "block" }}>
                    {c.label}{" "}
                    <span style={{ color: "#ff4d4f", fontWeight: 400 }}>*</span>
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {c.desc}
                  </Text>
                </div>
              </div>
              <StarRating
                value={ratings[c.key]}
                onChange={(v) => setRatings((prev) => ({ ...prev, [c.key]: v }))}
                classificacoes={classificacoes}
              />
            </div>
          ))}

          {/* Recomendação (opcional) */}
          <div
            style={{
              background: "#fafafa",
              borderRadius: 12,
              padding: "16px 20px",
              border: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  background: "#13c2c215",
                  color: "#13c2c2",
                  borderRadius: 8,
                  padding: "6px 8px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: 14,
                }}
              >
                <FaCommentAlt />
              </div>
              <div>
                <Text strong style={{ fontSize: 14, display: "block" }}>
                  Recomendação{" "}
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    (opcional)
                  </Text>
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sugestões ou feedback adicional para o colaborador
                </Text>
              </div>
            </div>
            <TextArea
              value={recomendacao}
              onChange={(e) => {
                if (e.target.value.length <= 200) setRecomendacao(e.target.value);
              }}
              placeholder="Ex: Continue com o excelente trabalho. Sugiro melhorar a documentação..."
              autoSize={{ minRows: 3, maxRows: 4 }}
              style={{ borderRadius: 10, fontSize: 13, resize: "none" }}
              showCount
              maxLength={200}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "16px 28px",
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {!allRated && (
            <span style={{ color: "#faad14" }}>
              ⚠ Avalie todos os critérios para aprovar
            </span>
          )}
        </Text>
        <div style={{ display: "flex", gap: 12 }}>
          <Button onClick={handleClose} style={{ borderRadius: 8 }}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleConfirm}
            disabled={!allRated}
            icon={<FaCheckCircle style={{ marginRight: 6 }} />}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              background: allRated ? "#52c41a" : undefined,
              borderColor: allRated ? "#52c41a" : undefined,
            }}
          >
            Aprovar Tarefa
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const AprovacaoTarefas = () => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [processingIds, setProcessingIds] = useState([]);

  // Modal states
  const [rejeitarModal, setRejeitarModal] = useState({ visible: false, id: null });
  const [avaliacaoModal, setAvaliacaoModal] = useState({ visible: false, id: null });
  const [classificacoes, setClassificacoes] = useState([]);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Aprovação de Tarefas | Dashboard";
  }, []);

  // Carregar classificações
  useEffect(() => {
    const loadClassificacoes = async () => {
      try {
        const data = await Api.ListClassificacao();
        setClassificacoes(Array.isArray(data) ? data : []);
      } catch {
        console.error("Erro ao carregar classificações");
      }
    };
    loadClassificacoes();
  }, []);

  const loadTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListTarefaAprovacao({ id_direcao: userStored.direcao });
      const list = Array.isArray(response) ? response : [];
      setTarefas(list);
    } catch {
      toast.error("Erro ao carregar tarefas para aprovação.");
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  useEffect(() => {
    loadTarefas();
  }, [loadTarefas]);

  /* ── Abrir modals ── */
  const openRejeitarModal = (id) => setRejeitarModal({ visible: true, id });
  const openAvaliacaoModal = (id) => setAvaliacaoModal({ visible: true, id });

  /* ── Confirmar Rejeição ── */
  const handleConfirmRejeitar = async (motivo) => {
    const id = rejeitarModal.id;
    try {
      setProcessingIds((prev) => [...prev, id]);
      setRejeitarModal({ visible: false, id: null });
      await Api.AprovarTarefa({ id, id_status: 1, motivo });
      toast.success("Tarefa rejeitada com sucesso!");
      loadTarefas();
    } catch {
      toast.error("Erro ao rejeitar tarefa.");
    } finally {
      setProcessingIds((prev) => prev.filter((pid) => pid !== id));
    }
  };

  /* ── Confirmar Aprovação com Avaliação ── */
  const handleConfirmAprovar = async ({
    qualidade_entrega,
    espirito_equipa,
    iniciativa_proatividade,
    resolucao_problema,
    recomendacao,
  }) => {
    const id = avaliacaoModal.id;
    try {
      setProcessingIds((prev) => [...prev, id]);
      setAvaliacaoModal({ visible: false, id: null });
      await Api.AprovarTarefa({
        id,
        id_status: 2,
        qualidade_entrega,
        espirito_equipa,
        iniciativa_proatividade,
        resolucao_problema,
        recomencao: recomendacao,
      });
      toast.success("Tarefa aprovada e avaliação registada com sucesso!");
      loadTarefas();
    } catch {
      toast.error("Erro ao aprovar tarefa.");
    } finally {
      setProcessingIds((prev) => prev.filter((pid) => pid !== id));
    }
  };

  // Filtragem
  const filtered = tarefas.filter((t) => {
    const q = search.toLowerCase();
    return (
      !q ||
      t.titulo?.toLowerCase().includes(q) ||
      t.descricao?.toLowerCase().includes(q) ||
      t.projeto?.toLowerCase().includes(q) ||
      t.nome?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: "Tarefa",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#fff7e6",
              color: "#1677ff",
              borderRadius: 8,
            }}
            icon={<FaTasks />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {titulo}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.descricao?.length > 60
                ? record.descricao.substring(0, 60) + "..."
                : record.descricao}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Projeto",
      dataIndex: "projeto",
      key: "projeto",
      width: 200,
      render: (projeto) => (
        <Tag color="blue" style={{ borderRadius: 20, padding: "4px 12px" }}>
          <FaProjectDiagram style={{ marginRight: 6 }} />
          {projeto}
        </Tag>
      ),
    },
    {
      title: "Responsável",
      dataIndex: "nome",
      key: "nome",
      width: 180,
      render: (nome) => (
        <Tag color="purple" style={{ borderRadius: 20, padding: "4px 12px" }}>
          <FaUser style={{ marginRight: 6 }} />
          {nome}
        </Tag>
      ),
    },
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "percentagem",
      width: 180,
      render: (percentagem) => {
        const valor = parseFloat(percentagem) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{
                "0%": "#1677ff",
                "100%": "#52c41a",
              }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => {
        const isProcessing = processingIds.includes(record.id);
        return (
          <Space>
            <Tooltip title="Aprovar & Avaliar">
              <Button
                type="text"
                icon={<FaCheckCircle />}
                style={{ color: "#52c41a", fontSize: 16 }}
                loading={isProcessing}
                onClick={() => openAvaliacaoModal(record.id)}
              />
            </Tooltip>
            <Tooltip title="Rejeitar">
              <Button
                type="text"
                icon={<FaTimesCircle />}
                style={{ color: "#ff4d4f", fontSize: 16 }}
                loading={isProcessing}
                onClick={() => openRejeitarModal(record.id)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout className="aprovacao-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Modals */}
        <RejeitarModal
          visible={rejeitarModal.visible}
          loading={processingIds.includes(rejeitarModal.id)}
          onConfirm={handleConfirmRejeitar}
          onCancel={() => setRejeitarModal({ visible: false, id: null })}
        />
        <AvaliacaoModal
          visible={avaliacaoModal.visible}
          loading={processingIds.includes(avaliacaoModal.id)}
          onConfirm={handleConfirmAprovar}
          onCancel={() => setAvaliacaoModal({ visible: false, id: null })}
          classificacoes={classificacoes}
        />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTasks style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Aprovação de Tarefas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Revise, avalie e valide as entregas da sua equipe
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Barra de pesquisa */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título, projeto ou responsável..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Tabela */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} tarefas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma tarefa pendente de aprovação" }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default AprovacaoTarefas;